import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, KeyRound, ShieldCheck, ArrowRight } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { apiLogin, apiVerifyOtp, formatApiError } from '../lib/auth';
import CaptchaWidget from '../components/CaptchaWidget';

export default function AdminLogin() {
  const { user, loading, loginSuccess } = useAdminAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [delivered, setDelivered] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const captchaRef = useRef(null);

  useEffect(() => {
    if (!loading && user) navigate('/admin/dashboard', { replace: true });
  }, [loading, user, navigate]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const captchaToken = captchaRef.current ? await captchaRef.current.getToken() : '';
      const res = await apiLogin(email.trim(), password, captchaToken);
      setStage('otp');
      setDelivered(!!res.delivered);
      if (res.dev_otp) { setDevOtp(res.dev_otp); setOtp(res.dev_otp); }
    } catch (err) {
      if (captchaRef.current) captchaRef.current.reset();
      setError(formatApiError(err.response?.data?.detail, 'Invalid email or password'));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await apiVerifyOtp(email.trim(), otp.trim());
      loginSuccess(res.token, res.user);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'Incorrect code'));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'h-11 w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none transition-colors focus:border-[#0035A8] focus:ring-2 focus:ring-[#0035A8]/20';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5 py-10">
      <div className="w-full max-w-md" data-testid="admin-login">
        <div className="mb-8 text-center">
          <img src="/BP-Mark.png" alt="BillPortal" className="mx-auto mb-3 h-14 w-14 object-contain" />
          <div className="text-xl font-bold tracking-tight text-slate-900">BillPortal</div>
          <p className="mt-1 text-sm text-slate-500">Marketing CRM · Admin sign in</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm">
          {stage === 'credentials' ? (
            <form onSubmit={submitCreds} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Email</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="admin-login-email"
                    placeholder="you@billportal.io"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    data-testid="admin-login-password"
                    placeholder="••••••••"
                    className={inputCls}
                  />
                </div>
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="admin-login-error">{error}</p>}
              <CaptchaWidget ref={captchaRef} className="pt-1" />
              <button
                type="submit" disabled={busy}
                data-testid="admin-login-submit"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#0035A8] text-sm font-semibold text-white transition-colors hover:bg-[#002B87] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="admin-otp-form">
              <div className="flex items-center gap-2 text-slate-700">
                <ShieldCheck className="h-5 w-5 text-[#0035A8]" />
                <span className="text-sm font-semibold">Two-factor verification</span>
              </div>
              <p className="text-sm text-slate-500">
                {delivered
                  ? <>We emailed a 6-digit code to <span className="font-medium text-slate-700">{email}</span>.</>
                  : <>Enter the 6-digit verification code to continue.</>}
              </p>
              {devOtp && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="admin-dev-otp">
                  Test mode: your code is <strong>{devOtp}</strong> (email delivery pending SES setup). Code <strong>000000</strong> also works.
                </div>
              )}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Verification code</label>
                <input
                  value={otp} onChange={(e) => setOtp(e.target.value)} required inputMode="numeric" maxLength={6}
                  data-testid="admin-otp-input"
                  placeholder="000000"
                  className="h-12 w-full rounded-md border border-slate-300 bg-white px-3 text-center text-lg font-bold tracking-[0.4em] text-slate-900 outline-none transition-colors focus:border-[#0035A8] focus:ring-2 focus:ring-[#0035A8]/20"
                />
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="admin-otp-error">{error}</p>}
              <button
                type="submit" disabled={busy}
                data-testid="admin-otp-submit"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#0035A8] text-sm font-semibold text-white transition-colors hover:bg-[#002B87] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & sign in'}
              </button>
              <button
                type="button" onClick={() => { setStage('credentials'); setOtp(''); setError(''); }}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
              >
                Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
